import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItemButton,
  TextField,
  Typography,
} from '@mui/material';
import { Armchair, Lock, Search, UserMinus, Users } from 'lucide-react';
import { FloorplanAssignment, FloorplanDesk, FloorplanEmployee, FloorplanLevel } from '../../types/floorplan';
import { EmptyState, IconTile, PersonCell, StatusPill } from '../ui';
import { sbg } from '../../theme/sbgTokens';
import { formatCount } from '../../utils/format';
import { deskInSentence, deskLabel, deskPlaceLabel, floorNameByDeskId, floorNamesWhenMixed } from './floorplan-panels/deskLabel';

interface DeskAssignmentDialogProps {
  open: boolean;
  desk: FloorplanDesk | null;
  assignment: FloorplanAssignment | null;
  employees: FloorplanEmployee[];
  assignmentsByEmployeeId: Map<string, FloorplanAssignment>;
  busy?: boolean;
  /** Optional: the layout's floors, so desks on a multi-floor layout name their floor. */
  levels?: FloorplanLevel[];
  onClose: () => void;
  onAssign: (employeeId: string) => Promise<void>;
  onClear: () => Promise<void>;
}

type PendingAction = { kind: 'assign'; employeeId: string } | { kind: 'clear' } | null;

const DeskAssignmentDialog: React.FC<DeskAssignmentDialogProps> = ({
  open,
  desk,
  assignment,
  employees,
  assignmentsByEmployeeId,
  busy = false,
  levels,
  onClose,
  onAssign,
  onClear,
}) => {
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState<PendingAction>(null);
  const [error, setError] = useState<string | null>(null);
  const pendingRef = useRef(false);
  const isBusy = busy || pending !== null;

  useEffect(() => {
    if (open) {
      setSearch('');
      setError(null);
    }
  }, [open, desk?.id]);

  const filteredEmployees = useMemo(() => {
    const query = search.trim().toLowerCase();
    return employees
      .filter((employee) => !query || employee.name.toLowerCase().includes(query)
        || employee.email.toLowerCase().includes(query)
        || (employee.teamName || '').toLowerCase().includes(query)
        || (employee.department?.name || '').toLowerCase().includes(query))
      .sort((left, right) => {
        const leftAssigned = assignmentsByEmployeeId.has(left.id);
        const rightAssigned = assignmentsByEmployeeId.has(right.id);
        if (leftAssigned !== rightAssigned) return leftAssigned ? 1 : -1;
        return left.name.localeCompare(right.name);
      });
  }, [assignmentsByEmployeeId, employees, search]);

  const runAction = async (next: Exclude<PendingAction, null>, action: () => Promise<void>) => {
    if (isBusy || pendingRef.current) return;
    pendingRef.current = true;
    setPending(next);
    setError(null);
    try {
      await action();
    } catch (failure: any) {
      setError(failure?.data?.error || failure?.message || 'Couldn’t update this desk. Nothing changed; try again.');
    } finally {
      pendingRef.current = false;
      setPending(null);
    }
  };

  // Desks are named by the number on the canvas ("Desk 102"), never the internal key.
  const deskName = deskInSentence(desk);
  const floorNames = useMemo(() => floorNamesWhenMixed(levels), [levels]);
  const deskFloorName = useMemo(() => (desk ? floorNameByDeskId(levels).get(desk.id) ?? null : null), [desk, levels]);
  const query = search.trim();
  const occupant = assignment?.employee ?? null;

  return (
    <Dialog open={open} onClose={() => { if (!isBusy && !pendingRef.current) onClose(); }} fullWidth maxWidth="sm"
      aria-labelledby="desk-assignment-title">
      <DialogTitle component="div" id="desk-assignment-header" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 2 }}>
        <IconTile size={40}>
          <Armchair />
        </IconTile>
        <Box sx={{ minWidth: 0 }}>
          <Typography id="desk-assignment-title" variant="h3" component="h2" sx={{ color: sbg.text.primary, fontVariantNumeric: 'tabular-nums' }}>
            {desk ? deskLabel(desk) : 'Desk assignment'}
          </Typography>
          {deskFloorName && (
            <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: 0.25 }}>
              {deskFloorName}
            </Typography>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 2.5 }}>
          <Box component="section" aria-label="Current occupant" sx={{ display: 'grid', gap: 1, p: 2, borderRadius: sbg.radius.lg, bgcolor: sbg.bg.surfaceSubtle }}>
            <Typography variant="overline" component="p" sx={{ color: sbg.text.secondary }}>
              Current occupant
            </Typography>
            {occupant ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, minWidth: 0 }}>
                <PersonCell
                  name={occupant.name}
                  secondary={occupant.teamName || occupant.department?.name || occupant.email}
                />
                <StatusPill tone="info">Assigned</StatusPill>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
                <Typography variant="body1" component="p" sx={{ color: sbg.text.primary }}>
                  No one sits here yet.
                </Typography>
                <StatusPill tone="neutral">Open</StatusPill>
              </Box>
            )}
          </Box>

          <Box component="section" aria-labelledby="desk-assignment-choose-title" sx={{ display: 'grid', gap: 1.25 }}>
            <Box>
              <Typography id="desk-assignment-choose-title" variant="h4" component="h3" sx={{ color: sbg.text.primary }}>
                {occupant ? 'Assign someone else' : 'Assign an employee'}
              </Typography>
              <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: 0.25 }}>
                {occupant
                  ? `Someone who has a desk swaps with ${occupant.name}. Someone without one leaves ${occupant.name} unassigned.`
                  : `Choose who sits at ${deskName}. People without a desk are listed first.`}
              </Typography>
            </Box>
            <TextField value={search} onChange={(event) => setSearch(event.target.value)} label="Search employees"
              placeholder="e.g. Maria Chen or Sales" fullWidth disabled={isBusy}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search size={16} aria-hidden="true" /></InputAdornment> }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="caption" component="p" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }} aria-live="polite">
                {query
                  ? `${formatCount(filteredEmployees.length, 'match', 'matches')}`
                  : formatCount(filteredEmployees.length, 'employee')}
              </Typography>
              <Typography variant="caption" component="p" sx={{ color: sbg.text.secondary }}>Unassigned first</Typography>
            </Box>
            <List disablePadding aria-label="Employees available for desk assignment"
              sx={{ maxHeight: 'min(360px, 45vh)', overflowY: 'auto', border: `1px solid ${sbg.border.default}`, borderRadius: sbg.radius.lg, p: 0.5 }}>
              {filteredEmployees.map((employee) => {
                const employeeAssignment = assignmentsByEmployeeId.get(employee.id);
                const canAssignEmployee = Boolean(employee.canEdit) && (!assignment || assignment.employee.canEdit);
                const isCurrentOccupant = assignment?.employeeId === employee.id;
                const isAssigning = pending?.kind === 'assign' && pending.employeeId === employee.id;
                return (
                  <Box component="li" key={employee.id} sx={{ listStyle: 'none' }}>
                    <ListItemButton component="button" selected={isCurrentOccupant} disabled={isBusy || !canAssignEmployee}
                      onClick={() => runAction({ kind: 'assign', employeeId: employee.id }, () => onAssign(employee.id))}
                      aria-label={`Assign ${employee.name} to ${deskName}`}
                      aria-busy={isAssigning || undefined}
                      sx={{ width: '100%', px: 1.25, py: 1, gap: 1.5, textAlign: 'left', alignItems: 'center', font: 'inherit',
                        // View-only rows stay readable (the "View only" label carries the state); rows dim only while a change is saving.
                        '&.Mui-disabled': { opacity: isBusy ? 0.56 : 1 } }}>
                      <PersonCell
                        name={employee.name}
                        secondary={`${employee.teamName || employee.department?.name || 'No team'} · ${employee.email}`}
                        sx={{ flex: 1 }}
                      />
                      <Box sx={{ flexShrink: 0, maxWidth: '45%', minWidth: 0, display: 'grid', justifyItems: 'end', gap: 0.5 }}>
                        {/* Where someone sits is plain text; only "This desk" is a state worth a pill. */}
                        {isCurrentOccupant ? (
                          <StatusPill tone="info">This desk</StatusPill>
                        ) : (
                          <Typography
                            variant="labelSm"
                            component="span"
                            noWrap
                            sx={{ maxWidth: '100%', color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}
                          >
                            {employeeAssignment
                              ? deskPlaceLabel(employeeAssignment.desk, floorNames.get(employeeAssignment.desk.levelId))
                              : 'No desk'}
                          </Typography>
                        )}
                        {isAssigning ? (
                          <Typography variant="caption" component="span" sx={{ color: sbg.text.secondary }}>Assigning…</Typography>
                        ) : !canAssignEmployee && (
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: sbg.text.secondary }}>
                            <Lock size={12} aria-hidden="true" />
                            <Typography variant="caption" component="span" sx={{ color: 'inherit' }}>View only</Typography>
                          </Box>
                        )}
                      </Box>
                    </ListItemButton>
                  </Box>
                );
              })}
              {filteredEmployees.length === 0 && (
                <Box component="li" sx={{ listStyle: 'none' }}>
                  {query ? (
                    <EmptyState
                      compact
                      title="No matching employees"
                      body={`No one matches “${query}”. Try a name, email, or team.`}
                      action={<Button variant="outlined" size="small" onClick={() => setSearch('')}>Clear search</Button>}
                    />
                  ) : (
                    <EmptyState
                      compact
                      icon={<Users />}
                      title="No employees available"
                      body="Employees appear here once they’re added to the roster."
                    />
                  )}
                </Box>
              )}
            </List>
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ pt: 2, borderTop: `1px solid ${sbg.border.subtle}` }}>
        {assignment && (
          <Button variant="outlined" color="error" startIcon={<UserMinus size={16} />}
            onClick={() => runAction({ kind: 'clear' }, onClear)} disabled={isBusy || !assignment.employee.canEdit}
            aria-busy={pending?.kind === 'clear' || undefined}>
            {pending?.kind === 'clear' ? 'Clearing…' : 'Clear desk'}
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="text" onClick={onClose} disabled={isBusy}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeskAssignmentDialog;
